import { BarChart as EChartsBarChart } from 'echarts/charts';
import {
  DatasetComponent,
  GridComponent,
  MarkLineComponent,
  TitleComponent,
  TooltipComponent,
} from 'echarts/components';
import { EChartsCoreOption, use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  BarChartData,
  BarClickEvent,
  ContentDimensions,
  formatValue,
  ModeOption,
  SeriesOverrides,
  ThresholdOptions,
  UnitOptions,
} from '@/core';
// Deeper imports needed to avoid circular deps
import { useVizPanelNotice } from '@/plugin-system/components/VizPanelContextProvider';
import { useAppInteractionLevel } from '@/plugin-system/runtime';
import { useChartsTheme } from '../context/ChartsProvider';
import { ChartApi, EChart, MouseEventsParameters } from '../EChart';
import { PanelTooltipWrapper } from '../PanelTooltip';
import {
  DEFAULT_PANEL_TOOLTIP_CONFIG,
  PANEL_TOOLTIP_CONFIG_PINNING_DISABLED,
  PanelTooltipConfig,
} from '../PanelTooltip/model';
import { getFormattedAxis } from '../utils';
import { BarChartTooltipContent } from './BarChartTooltipContent';
import { computeBandAtCoords, getBarColor, getThresholdMarkLine, getTooltipData } from './utils';

// eslint-disable-next-line react-hooks/rules-of-hooks
use([
  EChartsBarChart,
  GridComponent,
  DatasetComponent,
  TitleComponent,
  TooltipComponent,
  CanvasRenderer,
  MarkLineComponent,
]);

const BAR_GAP = 6;

export interface BarChartBaseProps extends ContentDimensions {
  data: BarChartData[] | null;
  maxValue?: number;
  unit?: UnitOptions;
  mode?: ModeOption;
  overrides?: SeriesOverrides;
  thresholds?: ThresholdOptions;
  primaryColor?: string;
  isCompareMode?: boolean;
  comparisonPeriodLabel?: string;
  hiddenBarsCount?: number;
  onBarClick?: (e: BarClickEvent) => void;
  tooltipConfig?: PanelTooltipConfig;
  panelKey: string;
}

/**
 * Use the context-aware `BarChart.Viz` instead.
 *
 * @deprecated
 */
export function BarChartBase(props: BarChartBaseProps) {
  const {
    width,
    height,
    data,
    maxValue,
    unit = { kind: 'Decimal' },
    mode = 'value',
    overrides,
    thresholds,
    primaryColor,
    isCompareMode,
    comparisonPeriodLabel,
    hiddenBarsCount,
    onBarClick,
    // Since onBarClick can be used for custom context menu, ensure pinning is disabled to avoid conflicts.
    // Eventually we should provide series / content menu actions as part of PanelTooltip.
    tooltipConfig = onBarClick ? PANEL_TOOLTIP_CONFIG_PINNING_DISABLED : DEFAULT_PANEL_TOOLTIP_CONFIG,
    panelKey,
  } = props;

  const handleEChartClick = useCallback(
    (params: MouseEventsParameters<unknown>) => {
      const nativeEvent = params.event?.event;
      if (!(nativeEvent instanceof MouseEvent)) return;
      onBarClick?.({
        name: params.name,
        event: { event: nativeEvent },
        data: { value: (params.data as { value?: number | null })?.value ?? null },
      });
    },
    [onBarClick]
  );

  const chartApiRef = useRef<ChartApi>();
  const chartsTheme = useChartsTheme();
  const { thresholds: thresholdColors } = chartsTheme;

  const appInteractionLevel = useAppInteractionLevel();
  const { setPanelNotice: setHiddenBarsNotice, deletePanelNotice: deleteHiddenBarsNotice } =
    useVizPanelNotice('hidden_bars');

  const getBandDataAtIndex = useMemo(() => {
    if (!data) return undefined;
    return (dataIndex: number) =>
      getTooltipData({ data, dataIndex, overrides, primaryColor, thresholds, thresholdColors, maxValue });
  }, [data, overrides, primaryColor, thresholds, thresholdColors, maxValue]);

  useEffect(() => {
    if (hiddenBarsCount !== undefined && hiddenBarsCount > 0) {
      const isUserEditable = appInteractionLevel === 'editable-view' || appInteractionLevel === 'editable-edit';

      const additionalEditMessage = ' Increase the "Limit" setting for this panel to show additional bars.';

      setHiddenBarsNotice({
        type: 'warning',
        message: `${hiddenBarsCount} additional bars not shown.${isUserEditable ? additionalEditMessage : ''}`,
      });
    } else {
      deleteHiddenBarsNotice();
    }
  }, [appInteractionLevel, deleteHiddenBarsNotice, hiddenBarsCount, setHiddenBarsNotice]);

  // Sets the height of the bars, but is named 'width' to align with the ECharts property.
  // Since our bars are horizontal, the width property actually controls the height.
  const BAR_MIN_WIDTH = isCompareMode ? 28 : 14;

  // common label styles for both regular and comparison bars
  const labelStyle = useMemo(
    () => ({
      show: true,
      position: 'right',
      formatter: (params: { data: number }) => {
        if (mode === 'percentage') {
          return (
            params.data &&
            formatValue(params.data, {
              kind: 'Percent',
              decimal_places: unit.decimal_places,
            })
          );
        }
        return params.data && formatValue(params.data, unit);
      },
      barMinWidth: BAR_MIN_WIDTH,
      barCategoryGap: BAR_GAP,
    }),
    [BAR_MIN_WIDTH, mode, unit]
  );

  // common item styles for both regular and comparison bars
  const itemStyle = useMemo(() => {
    return {
      // Other params: https://echarts.apache.org/en/option.html#series-bar.itemStyle.color
      color: (params: { name: string; color: string; value: number }) => {
        return getBarColor({
          itemStyleParams: params,
          overrides,
          primaryColor,
          thresholds,
          thresholdColors,
          maxValue,
        });
      },
    };
  }, [overrides, primaryColor, thresholds, thresholdColors, maxValue]);

  const option: EChartsCoreOption = useMemo(() => {
    if (data == null || !data.length) return chartsTheme.noDataOption;

    const values: Array<BarChartData['value']> = [];
    const labels: Array<BarChartData['label']> = [];
    const comparisonValues: Array<BarChartData['comparisonValue']> = [];
    data.forEach((datum: BarChartData) => {
      values.push(datum.value);
      labels.push(datum.label.replace(/[\r\n]+/g, '')); // strip out any newline characters to ensure labels do not overlap
      if (isCompareMode) {
        comparisonValues.push(datum.comparisonValue);
      }
    });

    return {
      animation: false,
      title: {
        show: false,
      },
      xAxis: getFormattedAxis(
        {
          show: true,
          axisLabel: {
            hideOverlap: true,
          },
        },
        unit
      ),
      yAxis: {
        type: 'category',
        splitLine: {
          show: false,
        },
        axisLabel: {
          overflow: 'truncate',
          width: width / 3,
        },
        data: labels,
      },
      series: [
        {
          type: 'bar',
          data: values,
          label: labelStyle,
          itemStyle: itemStyle,
          markLine: getThresholdMarkLine({ thresholds, thresholdColors, maxValue }),
        },
        isCompareMode && {
          type: 'bar',
          data: comparisonValues,
          label: labelStyle,
          itemStyle: {
            ...itemStyle,
            decal: chartsTheme.stripedDecal,
          },
        },
      ],
      tooltip: {
        show: true,
        showContent: false,
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
      },
      // Increase distance between grid and container to prevent y axis labels from getting cut off
      grid: {
        top: thresholds?.steps?.length ? 20 : 5,
        right: '3%',
        left: '3%',
      },
    };
  }, [
    data,
    chartsTheme.noDataOption,
    chartsTheme.stripedDecal,
    unit,
    width,
    labelStyle,
    itemStyle,
    isCompareMode,
    thresholds,
    thresholdColors,
    maxValue,
  ]);

  return (
    <PanelTooltipWrapper
      width={width}
      tooltipConfig={tooltipConfig}
      panelKey={panelKey}
      renderTooltip={({ mousePos, pinnedCoords, isOverChart, isPinned }) => {
        if (!tooltipConfig.enabled || !chartApiRef.current) return null;
        // Prevent multiple tooltips from showing since mousePos is shared through PanelTooltipProvider
        if (!isOverChart && !isPinned) return null;
        const currentMousePos = isPinned ? pinnedCoords : mousePos;
        if (!currentMousePos) return null;
        const bandData = computeBandAtCoords(chartApiRef.current.convertFromPixel, currentMousePos, {
          getBandDataAtIndex,
        });
        if (!bandData) return null;
        return (
          <BarChartTooltipContent
            data={bandData}
            unit={unit}
            mode={mode}
            isCompareMode={isCompareMode}
            comparisonPeriodLabel={comparisonPeriodLabel}
          />
        );
      }}
    >
      <EChart
        width={width}
        height={data ? Math.max(data.length * (BAR_MIN_WIDTH + BAR_GAP), height) : height}
        option={option}
        theme={chartsTheme.echartsTheme}
        onClick={onBarClick ? handleEChartClick : undefined}
        chartApiRef={chartApiRef}
      />
    </PanelTooltipWrapper>
  );
}
